import React from 'react';
import { Clock, MapPin, Users, Gift } from 'lucide-react';
import type { InvasionPoster } from '../../data/invasions';
import { EntityImage } from '../world/EntityImage';
import { entityImages } from '../../data/guide/entityImages';

/** Invasión → imagen de su jefe (misma entrada que en su ficha de monstruo). */
const bossImageKey: Record<string, string> = {
  'death-king': 'nombre:death-king',
  'red-dragon': 'monstruo:42',
  'white-wizard': 'nombre:white-wizard'
};
type Props = {
  id: string;
  name: string;
  poster: InvasionPoster;
  perDay: number;
  location: string;
  spawn: string;
  rewardsTitle: string;
  children: React.ReactNode;
};

/** Tarjeta de invasión inspirada en los pósters oficiales. En móvil: arte más bajo y filas compactas. */
export function InvasionPosterCard({
  id,
  name,
  poster,
  perDay,
  location,
  spawn,
  rewardsTitle,
  children
}: Props) {
  const boss = bossImageKey[id] ? entityImages.find(e => e.key === bossImageKey[id]) : undefined;
  return <li id={id} className="scroll-mt-20 md:scroll-mt-28" data-id="element-521" data-mp="1yzvdmdq2g2i2">
      <article className="flex h-full flex-col overflow-hidden rounded-2xl md:rounded-3xl border border-[#D4AF37]/30 bg-[#070c14]" data-id="element-522" data-mp="1z1g5fvkzx7c8">
        <div className="relative aspect-[2/1] overflow-hidden border-b border-[#D4AF37]/30" data-id="element-523" data-mp="1u34u1gf1yorw">
          <img src={poster.src} alt={`Arte de la invasión ${name}`} loading="lazy" className={`h-full w-full object-cover ${poster.hasTitle ? 'object-top' : 'object-center'}`} data-id="element-524" data-mp="1p3iircisbilq" />
          {poster.hasTitle ? <h3 className="sr-only" data-id="element-525" data-mp="2crrzi0155g60">{name}</h3> : <h3 className="absolute inset-x-0 bottom-2 md:bottom-3 text-center font-cinzel text-xl md:text-4xl font-black uppercase tracking-wide text-[#F3E3B3] [text-shadow:0_2px_12px_rgba(0,0,0,0.9)]" data-id="element-526" data-mp="2801p5u27esel">
              {name}
            </h3>}
          <p className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full border border-[#D4AF37]/60 bg-[#050b14]/85 px-2 py-0.5 font-cinzel text-[11px] font-bold uppercase text-[#E8C766] md:hidden" data-id="element-527" data-mp="30ixfcf7308gb">
            <Clock className="h-3 w-3" aria-hidden="true" data-id="element-528" data-mp="yrowhbaz5pei" />
            {perDay}× al día
          </p>
        </div>

        <div className="flex flex-1 flex-col p-3 md:p-5" data-id="element-529" data-mp="k329qoj0pvd9">
          <p className="hidden md:flex items-center justify-center gap-2.5 rounded-xl border border-[#D4AF37]/50 bg-[#D4AF37]/[0.06] px-3 py-2.5 font-cinzel text-base font-bold uppercase text-[#E8C766]" data-id="element-530" data-mp="k2iqn4hmt0xn">
            <Clock className="h-5 w-5" aria-hidden="true" data-id="element-531" data-mp="b471z80bsd7u" />
            {perDay} veces al día
          </p>

          {boss?.image && <div className="mb-2 md:mt-3 md:mb-0 flex items-center gap-2.5" data-id="element-532" data-mp="14g4cxggxi3j">
              <EntityImage entry={boss} alt={boss.label} name={boss.label} data-id="element-533" data-mp="62zt15mg57zl" />
              <p className="text-[13px] md:text-sm text-gray-200" data-id="element-534" data-mp="653ttep1tqvn">
                <span className="block font-cinzel text-[10px] md:text-[11px] font-bold uppercase tracking-wide text-[#D4AF37]" data-id="element-535" data-mp="1viz4wuhg9bix">Jefe</span>
                {boss.label}
              </p>
            </div>}
          <dl className="md:mt-3 flex flex-1 flex-col divide-y divide-[#D4AF37]/15" data-id="element-536" data-mp="3kzkyp012dg3w">
            <div className="grid grid-cols-2 divide-x divide-[#D4AF37]/15 md:block md:divide-x-0 md:divide-y md:divide-[#D4AF37]/15" data-id="element-537" data-mp="3g1b3wqj94hit">
              <Row icon={MapPin} label="Dónde aparece" data-id="element-538" data-mp="3uqz6rw72zo8n">{location}</Row>
              <Row icon={Users} label="En cada aparición" padLeft data-id="element-539" data-mp="3pxe7m0tr4o7v">{spawn}</Row>
            </div>
            <Row icon={Gift} label={rewardsTitle} grow data-id="element-540" data-mp="7ivf29u7gmih">{children}</Row>
          </dl>
        </div>
      </article>
    </li>;
}
function Row({
  icon: Icon,
  label,
  children,
  grow,
  padLeft
}: {
  icon: typeof MapPin;
  label: string;
  children: React.ReactNode;
  grow?: boolean;
  padLeft?: boolean;
}) {
  return <div className={`grid grid-cols-[16px_1fr] md:grid-cols-[28px_1fr] gap-2 md:gap-3 py-2 md:py-3 ${grow ? 'flex-1' : ''} ${padLeft ? 'pl-2.5 md:pl-0' : 'pr-2 md:pr-0'}`} data-id="element-541" data-mp="cjswulbazejg">
      <Icon className="mt-0.5 h-4 w-4 md:h-5 md:w-5 text-[#D4AF37]" aria-hidden="true" data-id="element-542" data-mp="2ebef13db2tr8" />
      <div className="min-w-0 md:border-l md:border-[#D4AF37]/20 md:pl-3" data-id="element-543" data-mp="15e7kdtoo4mqd">
        <dt className="font-cinzel text-[10px] md:text-[13px] font-bold uppercase tracking-wide text-[#D4AF37]" data-id="element-544" data-mp="1acsklt3x76et">{label}</dt>
        <dd className="mt-0.5 md:mt-1 text-[13px] md:text-[15px] leading-snug text-gray-100" data-id="element-545" data-mp="1fbca23hq30bx">{children}</dd>
      </div>
    </div>;
}